import React, { useEffect, useMemo, useState } from 'react';
import './style.scss';
import Accordion from 'react-bootstrap/Accordion';
import InputGroup from 'react-bootstrap/InputGroup';
import Form from 'react-bootstrap/Form';
import { FaSearch } from 'react-icons/fa';
import 'bootstrap/dist/css/bootstrap.min.css';
import Sidebar from '../../../components/sidebar';
import { useOutletContext } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getHelpCenterData } from '../../../core/data/redux/helpCenter/_helpCenterAction';
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext';

const HelpCenter = () => {
  const { isSidebarOpen, onToggleSidebar } = useOutletContext<{ isSidebarOpen: boolean; onToggleSidebar: () => void }>();
  const dispatch: any = useDispatch();

  const helpCenterDetails = useSelector((state: any) => state?.helpCenter);
  const helpCenterData = helpCenterDetails?.helpCenterData || {};

  const uncategorizedVideos = helpCenterData?.uncategorized?.videos || [];
  const uncategorizedLinks = helpCenterData?.uncategorized?.links || [];
  const videoList: any[] = useMemo(() => {
    const mergedList = Array.isArray(helpCenterData)
      ? helpCenterData
      : [...uncategorizedVideos, ...uncategorizedLinks];

    const uniqueList = mergedList.filter((item, index, arr) =>
      item?.uuid
        ? arr.findIndex((v) => v?.uuid === item.uuid) === index
        : arr.indexOf(item) === index
    );

    return uniqueList.sort((a, b) => {
      const aTime = new Date(a?.created_at || 0).getTime();
      const bTime = new Date(b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [helpCenterData, uncategorizedLinks, uncategorizedVideos]);

  const [searchTerm, setSearchTerm] = useState('');
  const [activeKey, setActiveKey] = useState<string | null>(null);

  const getYoutubeThumbnail = (url: string): string => {
    if (!url) return '';
    const id =
      url.match(/youtu\.be\/([a-zA-Z0-9_-]+)/)?.[1] ||
      url.match(/youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)/)?.[1];
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
  };

  const isYoutubeUrl = (url: string): boolean => /youtu\.be|youtube\.com/.test(url || '');
  useEffect(() => {
    dispatch(getHelpCenterData({}))
  }, [dispatch]);

  useEffect(() => {
    if (helpCenterData?.categories?.length > 0) {
      setActiveKey("0-0");
    }
  }, [helpCenterData]);

  const filteredFaqs = (helpCenterData?.questions || []).filter((faq: any) =>
    !searchTerm || faq?.question?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredCategories = (helpCenterData?.categories || []).map(
    (category: any) => ({
      ...category,
      questions: category.questions.filter((faq: any) =>
        !searchTerm ||
        faq?.question?.toLowerCase().includes(searchTerm.toLowerCase())
      ),
    })
  ).filter((category: any) => category.questions.length > 0);

  const handleToggle = (eventKey: AccordionEventKey) => {
    if (eventKey === "0") {
      setActiveKey("0");
    } else {
      setActiveKey(activeKey === eventKey ? null : (eventKey as string | null));
    }
  };

  return (
    <div className="layout">
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} />

      {/* Main Content */}
      <main className="content mb-5">

        <div className="help-center profileSettings p-4">
          <h2 className="mb-4">Help Center</h2>

          <div className="help-card p-4 rounded shadow-sm bg-white">
            <InputGroup className="mb-3 search-input">
              <InputGroup.Text>
                <FaSearch />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search regarding support"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            {videoList.length > 0 && (
              <div className="mb-4">
                <h5 className="mb-3">Tutorial Videos</h5>
                <div className="row g-3">
                  {videoList.map((video: any) => {
                    const videoUrl = video?.video_url || '';
                    const thumbUrl = getYoutubeThumbnail(videoUrl);
                    const isYoutube = isYoutubeUrl(videoUrl);
                    return (
                      <div key={video?.uuid} className="col-12 col-sm-6 col-md-4">
                        <a
                          href={videoUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-decoration-none d-block rounded overflow-hidden border shadow-sm bg-dark position-relative"
                          style={{ aspectRatio: '16/9' }}
                        >
                          {thumbUrl ? (
                            <img
                              src={thumbUrl}
                              alt="Video thumbnail"
                              className="w-100 h-100 object-fit-cover"
                            />
                          ) : !isYoutube ? (
                            <video
                              src={videoUrl}
                              className="w-100 h-100 object-fit-cover"
                              preload="metadata"
                              muted
                              playsInline
                            />
                          ) : (
                            <div className="w-100 h-100 d-flex align-items-center justify-content-center text-white">
                              <span>Video</span>
                            </div>
                          )}
                          <div className="position-absolute bottom-0 start-0 end-0 p-2 bg-dark bg-opacity-75 text-white small text-center">
                            Watch Video
                          </div>
                        </a>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className=" p-4 rounded shadow-sm bg-white">
              <div className="mt-1">
                <h5 className="mb-3">
                  Help Center – Frequently Asked Questions
                </h5>
              </div>

              <Accordion activeKey={activeKey} onSelect={handleToggle} flush>
                {filteredCategories.map((category: any, catIndex: number) => (
                  <div key={category.uuid} className="mb-4">

                    {/* Category Title */}
                    <h5 className="mb-3 fw-bold">
                      {category.category_name}
                    </h5>

                    {category.questions.map((faq: any, qIndex: number) => {
                      const eventKey = `${catIndex}-${qIndex}`;

                      return (
                        <Accordion.Item eventKey={eventKey} key={faq.uuid}>
                          <Accordion.Header>{faq.question}</Accordion.Header>
                          <Accordion.Body>
                            <div
                              dangerouslySetInnerHTML={{ __html: faq.answer }}
                            />
                          </Accordion.Body>
                        </Accordion.Item>
                      );
                    })}
                  </div>
                ))}
              </Accordion>

              {/* <Accordion activeKey={activeKey} onSelect={handleToggle} flush>
                {filteredFaqs.map((faq: any, index: any) => (
                  <Accordion.Item eventKey={index.toString()} key={index}>
                    <Accordion.Header>{faq.question}</Accordion.Header>
                    <Accordion.Body>{faq.answer}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion> */}

            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default HelpCenter;
